import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api-client";

// --- Types ---

export interface TenantUserSummary {
  id: string;
  name: string;
  email: string;
  isActive: boolean;
}

// --- List hook ---

/**
 * The tenant's active users. Mirrors useRoles's list-hook shape. The notify
 * target-user picker (RouteConfigBody) offers these — see Task 13 in
 * `.superpowers/sdd/2026-08-07-s1-email-notifications/`.
 */
export function useUsers() {
  const [users, setUsers] = useState<TenantUserSummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await api.get<{ users: TenantUserSummary[] }>("/users");
      setUsers(data.users.filter((u) => u.isActive));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load users");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return { users, isLoading, error, refetch: load };
}
